import {
  Card,
  CardBody,
  Col,
  Row,
  Spinner,
  Breadcrumb,
  BreadcrumbItem
} from 'reactstrap';
import SearchInput from 'components/SearchInput';
import React, { useCallback, useMemo, useState } from 'react';
import useUser from 'hooks/useUser';
import TypeResource from 'enum/TypeResource';
import TypeAction from 'enum/TypeAction';
import { useApiGet } from 'hooks/useApi';
import MediaFile from 'models/MediaFile';
import FormMediaModal from 'components/MediaGrid/FormMediaModal';
import Button from 'components/Button';
// @ts-ignore
import { DropTargetMonitor, useDrop } from 'react-dnd';
// @ts-ignore
import { NativeTypes } from 'react-dnd-html5-backend';
import MediaFilesGrid from 'components/MediaGrid/MediaFilesGrid';
import useUploadMedia from 'hooks/useUploadMedia';
import FileInputButton from 'components/FileInputButton';
import Icon from 'components/Icon';

type MediaGridProps = {
  title: string;
};

type MediaFileFilter = {
  search?: string;
  parent?: string;
};
const MediaGrid = ({ title }: MediaGridProps) => {
  const user = useUser();
  const [mediaFormItem, setMediaFormItem] = useState<MediaFile>();
  const [filteredData, setFilteredData] = React.useState<MediaFileFilter>({});
  const [parentsPaths, setParentsPaths] = React.useState<MediaFile['parent'][]>(
    []
  );
  const { data: medias, isLoading, refetch } = useApiGet<MediaFile[]>(
    '/media-manager/medias',
    {
      params: {
        parent: filteredData?.parent,
        search: filteredData?.search
      }
    }
  );
  const uploadFiles = useUploadMedia();
  const userCanAdd = useMemo(() => {
    return user?.resources?.[TypeResource.MEDIAS]?.includes(TypeAction.CREATE);
  }, [user]);

  const handleSearch = useCallback(
    (search: string) => {
      setFilteredData({ ...filteredData, search });
    },
    [filteredData]
  );
  const goToParent = useCallback((newParent: MediaFile['parent']) => {
    setParentsPaths((paths) => [...paths, newParent]);
    setFilteredData((data) => ({ ...data, parent: newParent?.id }));
  }, []);

  const goBackToParent = useCallback(
    (backParent: MediaFile['parent']) => {
      if (backParent) {
        const index = parentsPaths.findIndex(
          (path) => path?.id === backParent?.id
        );
        setParentsPaths(parentsPaths.slice(0, index));
      } else {
        setParentsPaths([]);
      }
      setFilteredData((data) => ({ ...data, parent: backParent?.id }));
    },
    [parentsPaths]
  );

  const handleFolderChange = useCallback(() => {
    setMediaFormItem(undefined);
    refetch();
  }, [refetch]);

  const handleUploadFiles = useCallback(
    (files?: File[] | null) => {
      if (files) {
        uploadFiles(files, {
          extraData: {
            payload: { parent_id: filteredData?.parent }
          },
          callback: () => refetch()
        });
      }
    },
    [filteredData?.parent, refetch, uploadFiles]
  );

  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop(item: { files: File[] }) {
        const files = item.files;
        handleUploadFiles(files);
      },
      canDrop: () => Boolean(userCanAdd),
      collect: (monitor: DropTargetMonitor) => {
        return {
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop()
        };
      }
    }),
    [handleUploadFiles, userCanAdd]
  );

  return (
    <>
      <div className="h-100 d-flex flex-column flex-fill">
        <Row className="d-flex flex-md-row flex-column align-items-center justify-content-between mt-2">
          <Col>
            <SearchInput
              onSearchTextChange={handleSearch}
              placeholder="Buscar"
              optionalsButtons
            />
          </Col>
          <Col className="text-end mt-md-2">
            {userCanAdd && (
              <>
                <Button
                  type="button"
                  className="button-outline me-2 button-campaign"
                  onClick={() =>
                    setMediaFormItem({
                      isFolder: true,
                      parent: { id: filteredData?.parent }
                    })
                  }
                >
                  Criar Pasta
                </Button>
                <FileInputButton
                  to={'/access-policies/create'}
                  className="button-register button-campaign "
                  accept="image/*,video/*"
                  onFilesChange={handleUploadFiles}
                ></FileInputButton>
              </>
            )}
          </Col>
        </Row>
        <Breadcrumb>
          {!filteredData?.search ? (
            <>
              <BreadcrumbItem
                active={!filteredData?.parent}
                role="button"
                className="d-flex justify-content-center align-items-center mt-3"
                onClick={() =>
                  filteredData?.parent && goBackToParent(undefined)
                }
              >
                <Icon icon="house-door" className="me-1 text-primary" /> Mídias
              </BreadcrumbItem>
              {parentsPaths.map((path) => (
                <BreadcrumbItem
                  className="mt-3"
                  key={path?.id}
                  role="button"
                  active={path?.id === filteredData?.parent}
                  onClick={() =>
                    path?.id !== filteredData?.parent && goBackToParent(path)
                  }
                >
                  {path?.name}
                </BreadcrumbItem>
              ))}
            </>
          ) : (
            <BreadcrumbItem
              active
              className="d-flex justify-content-center align-items-center"
            >
              Resultado da pesquisa:
            </BreadcrumbItem>
          )}
        </Breadcrumb>
        <div className="media-list" ref={drop}>
          <Card
            color="light"
            className="h-100 overflow-y-scroll flex-fill d-flex mt-1 media-list-item"
          >
            {isLoading && (
              <div className="position-absolute h-100 w-100 top-0 start-0 bg-gray-200 opacity-1 z-3 d-flex justify-content-center align-items-center">
                <Spinner color="primary">Loading...</Spinner>
              </div>
            )}
            {canDrop && isOver && userCanAdd && (
              <div className="position-absolute h-100 w-100 top-0 start-0 bg-danger-subtle opacity-4 fs-5 z-3 d-flex justify-content-center align-items-center text-dark">
                Solte os arquivos aqui
              </div>
            )}
            <MediaFilesGrid
              medias={medias}
              reFetchMedias={refetch}
              onEditMedia={setMediaFormItem}
              onGoToFolder={goToParent}
            />
            {medias?.length === 0 && (
              <CardBody className="d-flex justify-content-center align-items-center">
                Nenhuma mídia encontrada, arraste e solte arquivos para o upload
                de mídias
              </CardBody>
            )}
          </Card>
        </div>
      </div>
      <FormMediaModal
        mediaFormData={mediaFormItem}
        onCancel={() => setMediaFormItem(undefined)}
        onSave={handleFolderChange}
      />
    </>
  );
};

export default MediaGrid;
